import * as React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getLinkToMission } from '../getLinkToMission';
import { MissionLight } from '@wwf/sdk';
import { MissionStatusChip } from '../chips/MissionStatusChip';
import { MissionTypeChip } from '../chips/MissionTypeChip';
import { ReportIcon } from '../ReportIcon';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import { MediaIcon } from '@app/media/MediaIcon';
import { MemberIcon } from '@app/member/MemberIcon';
import { AntennaLightCard } from '@app/antenna/show/AntennaLightCard';
import { MemberLightCard } from '@app/member/show/MemberLightCard';
import { SessionCount } from '../chips/SessionCount';
import { MissionVisibilityIcon } from '../MissionVisibilityIcon';

export const MissionCalendarItem = ({
    mission,
    viewMode,
}: {
    mission: MissionLight;
    viewMode: 'month' | 'week' | 'day';
}) => {
    const navigate = useNavigate();

    return (
        <Box
            onClick={e => {
                e.stopPropagation();
                if (mission.id) {
                    navigate(getLinkToMission(mission.id));
                }
            }}
            sx={{
                p: 0.5,
                mb: 0.5,
                borderRadius: 1,
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                cursor: 'pointer',
                '&:hover': {
                    bgcolor: 'action.hover',
                    '& .hover-content': {
                        display: 'flex',
                    },
                },
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                overflow: 'hidden',
            }}
        >
            <Typography
                variant="caption"
                fontWeight="bold"
                title={mission.name || ''}
                sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    lineHeight: 1.2,
                }}
            >
                {mission.name}
            </Typography>
            <Box display="flex" gap={0.5} flexWrap="wrap">
                {mission.status && (
                    <MissionStatusChip status={mission.status} />
                )}
                <MissionTypeChip type={mission.type} />

                <Box
                    className="hover-content"
                    sx={{
                        display: viewMode === 'month' ? 'none' : 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                        width: '100%',
                    }}
                >
                    <AntennaLightCard
                        record={mission.antenna ?? undefined}
                        noLink
                    />
                    <MemberLightCard
                        record={mission.contact ?? undefined}
                        noLink
                    />
                </Box>
                <Stack
                    direction="row"
                    gap={1}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                >
                    {mission.whoCanSee && (
                        <MissionVisibilityIcon visibility={mission.whoCanSee} />
                    )}
                    <SessionCount count={mission.nbSessions ?? 0} />
                    {(mission.participantIds?.length ?? 0) > 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.2,
                            }}
                        >
                            <Typography variant="subtitle2">
                                {mission.participantIds?.length ?? 0}
                            </Typography>
                            <MemberIcon />
                        </Box>
                    )}

                    {(mission.nbMedias ?? 0) > 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.2,
                            }}
                        >
                            <Typography variant="subtitle2">
                                {mission.nbMedias ?? 0}
                            </Typography>
                            <MediaIcon />
                        </Box>
                    )}

                    {(mission.totalTouchedPersonCount ?? 0) > 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.2,
                            }}
                        >
                            <Typography variant="subtitle2">
                                {mission.totalTouchedPersonCount}
                            </Typography>
                            <PeopleOutlineIcon />
                        </Box>
                    )}

                    {mission.reportSentDate ? <ReportIcon /> : null}
                </Stack>
            </Box>
        </Box>
    );
};
